import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Package, AlertTriangle, TrendingDown, Boxes, SlidersHorizontal, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, inr, imgUrl, fmtDate, money } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [movements, setMovements] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [tags, setTags] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjForm, setAdjForm] = useState({ warehouse_id: "", new_qty: "", reason: "" });
  const [adjSaving, setAdjSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [st, wh, mv, tg, lg] = await Promise.all([
        API.get("/inventory/stock"),
        API.get("/warehouses"),
        API.get("/inventory/movements", { params: { product_id: id } }),
        API.get("/inventory/tags"),
        API.get(`/inventory/products/${id}/ledger`),
      ]);
      const p = st.data.find((x) => x.id === id);
      if (!p) setNotFound(true);
      setProduct(p);
      setWarehouses(wh.data);
      setMovements(mv.data);
      setTags(tg.data);
      setLedger(lg.data);
    } catch {
      setNotFound(true);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const canAdjust = ["admin", "stock_head"].includes(user?.role);

  const openAdjust = () => {
    setAdjForm({ warehouse_id: warehouses[0]?.id || "", new_qty: "", reason: "" });
    setAdjOpen(true);
  };

  const saveAdjustment = async () => {
    if (adjSaving) return;
    if (!adjForm.warehouse_id) return toast.error("Select a warehouse");
    if (adjForm.new_qty === "" || Number(adjForm.new_qty) < 0) return toast.error("Enter the counted quantity");
    setAdjSaving(true);
    try {
      await API.post("/inventory/adjust", {
        product_id: id,
        warehouse_id: adjForm.warehouse_id,
        new_qty: Number(adjForm.new_qty),
        reason: adjForm.reason,
      });
      setAdjOpen(false);
      toast.success("Stock adjusted — recorded in activity");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not adjust stock");
    } finally {
      setAdjSaving(false);
    }
  };

  if (notFound) {
    return (
      <div className="space-y-4" data-testid="product-detail-not-found">
        <Button variant="outline" size="sm" asChild><Link to="/inventory"><ArrowLeft className="mr-1 h-4 w-4" /> Back to Inventory</Link></Button>
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-sm text-slate-500">Product not found.</div>
      </div>
    );
  }
  if (!product) return <div className="text-sm text-slate-500" data-testid="product-detail-loading">Loading product...</div>;

  const tagStyle = (name) => {
    const c = tags.find((t) => t.name === name)?.color || "#64748B";
    return { backgroundColor: c + "1A", color: c, borderColor: c + "66" };
  };

  const ppc = product.pcs_per_ctn || 0;
  const ctnText = (pcs) => {
    if (ppc <= 0) return "";
    const c = Math.floor(pcs / ppc);
    const rem = pcs % ppc;
    return rem ? `${c} ctn + ${rem} pcs` : `${c} ctn`;
  };

  const adjCurrent = product.stock_by_warehouse?.[adjForm.warehouse_id] ?? 0;
  const adjDelta = adjForm.new_qty === "" ? null : Number(adjForm.new_qty) - adjCurrent;

  return (
    <div className="space-y-6" data-testid="product-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" asChild data-testid="product-detail-back">
          <Link to="/inventory"><ArrowLeft className="h-4 w-4" /></Link>
        </Button>
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">{product.name}</h1>
          <p className="text-sm text-slate-500">{product.sku ? `SKU: ${product.sku} · ` : ""}{product.unit}{ppc > 0 ? ` · 1 ctn = ${ppc} pcs` : ""}</p>
        </div>
        {canAdjust && (
          <Button variant="outline" className="ml-auto" onClick={openAdjust} data-testid="adjust-stock-button">
            <SlidersHorizontal className="mr-1.5 h-4 w-4" /> Adjust Stock
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-slate-200">
          <CardContent className="p-5">
            {product.image_url ? (
              <img src={imgUrl(product.image_url)} alt={product.name} className="h-48 w-full rounded-lg border border-slate-200 object-cover" data-testid="product-detail-image" />
            ) : (
              <div className="flex h-48 w-full items-center justify-center rounded-lg bg-slate-100 text-slate-400"><Package className="h-12 w-12" /></div>
            )}
            {product.description && <p className="mt-4 text-sm text-slate-600">{product.description}</p>}
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-md bg-slate-50 p-3"><div className="text-xs text-slate-500">Purchase Price</div><div className="font-semibold">{money(product.cost_price)}</div></div>
              <div className="rounded-md bg-slate-50 p-3"><div className="text-xs text-slate-500">Sale Price</div><div className="font-semibold">{money(product.sell_price)}</div></div>
              <div className="rounded-md bg-slate-50 p-3"><div className="text-xs text-slate-500">Stock Quantity</div><div className="font-semibold" data-testid="product-detail-total-stock">{product.total_stock} {product.unit}</div></div>
              <div className="rounded-md bg-slate-50 p-3"><div className="text-xs text-slate-500">Reserved</div><div className="font-semibold" data-testid="product-detail-reserved">0 {product.unit}</div></div>
              <div className="rounded-md bg-slate-50 p-3"><div className="text-xs text-slate-500">Available for Sale</div><div className="font-semibold" data-testid="product-detail-available">{product.total_stock} {product.unit}</div></div>
              <div className="rounded-md bg-slate-50 p-3"><div className="text-xs text-slate-500">Stock Value</div><div className="font-semibold">{money(product.stock_value)}</div></div>
            </div>
            {ppc > 0 && (
              <div className="mt-3 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2.5" data-testid="product-detail-ctn-summary">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-indigo-600">
                  <Boxes className="h-3.5 w-3.5" /> Carton Breakdown
                </div>
                <div className="mt-1.5 flex items-center justify-between text-sm">
                  <span className="font-semibold text-indigo-800">{product.total_stock} pcs = {ctnText(product.total_stock)}</span>
                  <span className="text-xs text-indigo-500">1 ctn = {ppc} pcs</span>
                </div>
              </div>
            )}
            <div className="mt-3 rounded-md border border-slate-200 p-3" data-testid="product-detail-forecast">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <TrendingDown className="h-3.5 w-3.5" /> Stock Forecast
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-slate-600">Avg daily outbound: {product.avg_daily_outbound}</span>
                <span className="text-slate-600">Days of stock: {product.days_of_stock ?? "-"}</span>
              </div>
              {product.low_stock && (
                <Badge className="mt-2 bg-amber-100 text-amber-700 hover:bg-amber-100" data-testid="product-detail-low-stock">
                  <AlertTriangle className="mr-1 h-3 w-3" /> Low stock — under 15 days of average outbound
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-lg border border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900">Stock by Warehouse</div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Warehouse</TableHead>
                  <TableHead className="text-right">Stock</TableHead>
                  <TableHead className="text-right">Value (cost)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="product-detail-stock-table">
                {warehouses.map((w) => {
                  const q = product.stock_by_warehouse?.[w.id] ?? 0;
                  return (
                    <TableRow key={w.id}>
                      <TableCell className="font-medium">{w.name}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={q > 0 ? "secondary" : "outline"}>{q}</Badge>
                        {ppc > 0 && q > 0 && (
                          <div className="mt-0.5 text-[11px] text-slate-400" data-testid={`warehouse-ctn-${w.id}`}>= {ctnText(q)}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{product.cost_price === null ? "—" : inr(q * (product.cost_price || 0))}</TableCell>
                    </TableRow>
                  );
                })}
                {warehouses.length === 0 && (
                  <TableRow><TableCell colSpan={3} className="py-8 text-center text-sm text-slate-500">No warehouses yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900">Recent Activity</div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Warehouse</TableHead>
                  <TableHead>Tag</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead>Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="product-detail-activity">
                {movements.slice(0, 15).map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>{fmtDate(m.created_at)}</TableCell>
                    <TableCell>
                      <Badge className={m.type === "in" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-rose-100 text-rose-700 hover:bg-rose-100"}>
                        {m.type === "in" ? "Inbound" : "Outbound"}
                      </Badge>
                    </TableCell>
                    <TableCell>{warehouses.find((w) => w.id === m.warehouse_id)?.name || "-"}</TableCell>
                    <TableCell>{m.tag ? <Badge variant="outline" style={tagStyle(m.tag)}>{m.tag}</Badge> : "-"}</TableCell>
                    <TableCell className="text-right">{m.qty}</TableCell>
                    <TableCell className="text-right">{money(m.rate)}</TableCell>
                    <TableCell className="max-w-40 truncate">{m.note || "-"}</TableCell>
                  </TableRow>
                ))}
                {movements.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-slate-500">No activity yet for this product.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-900">Item Transactions</div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Invoice/Ref No.</TableHead>
                  <TableHead>Party</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">Price/Unit</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="product-ledger-table">
                {ledger.map((t, i) => (
                  <TableRow key={i}>
                    <TableCell>{t.date || "-"}</TableCell>
                    <TableCell><Badge variant="outline" className={t.kind === "Delivery Challan" ? "border-indigo-300 text-indigo-700" : ""}>{t.kind}</Badge></TableCell>
                    <TableCell className="max-w-40 truncate">{t.ref || "-"}</TableCell>
                    <TableCell>{t.party || "-"}</TableCell>
                    <TableCell className={`text-right font-medium ${t.qty < 0 ? "text-rose-600" : "text-emerald-700"}`}>{t.qty > 0 ? "+" : ""}{t.qty}</TableCell>
                    <TableCell className="text-right">{t.rate == null ? "—" : inr(t.rate)}</TableCell>
                    <TableCell className="capitalize text-slate-500">{t.status || "-"}</TableCell>
                  </TableRow>
                ))}
                {ledger.length === 0 && <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-slate-500">No transactions yet for this item.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent data-testid="adjust-stock-dialog">
          <DialogHeader>
            <DialogTitle>Adjust Stock — {product.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Warehouse</Label>
              <Select value={adjForm.warehouse_id} onValueChange={(v) => setAdjForm({ ...adjForm, warehouse_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="adjust-warehouse-select"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>{w.name} — current: {product.stock_by_warehouse?.[w.id] ?? 0} {product.unit}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Counted Quantity ({product.unit})</Label>
              <Input type="number" min="0" value={adjForm.new_qty} onChange={(e) => setAdjForm({ ...adjForm, new_qty: e.target.value })} className="mt-1.5" data-testid="adjust-qty-input" />
              {adjDelta !== null && (
                <p className="mt-1 text-[11px] font-medium text-indigo-600" data-testid="adjust-delta-hint">
                  {adjDelta === 0 ? "No change vs current stock" : adjDelta > 0 ? `+${adjDelta} ${product.unit} will be added` : `${Math.abs(adjDelta)} ${product.unit} will be reduced`}
                </p>
              )}
            </div>
            <div>
              <Label>Reason</Label>
              <Input value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} placeholder="Damage / count mismatch / audit" className="mt-1.5" data-testid="adjust-reason-input" />
            </div>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={saveAdjustment} disabled={adjSaving} data-testid="adjust-confirm-button">
              {adjSaving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {adjSaving ? "Saving..." : "Confirm Adjustment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
